// src/pages/ScanPage.js
import React, { useState } from 'react';
import DateInput from '../components/DateInput';
import DownloadButtons from '../components/DownloadButtons';
import Sketch from '../Sketch';
import AsteroidScene3D from '../components/AsteroidScene3D';
import { fetchAsteroidData } from '../AsteroidAPI';
import '../AstroStyle.css';
import AsteroidMinter from "../components/AsteroidMinter";

export default function ScanPage() {
    const [date, setDate] = useState('');
    const [asteroids, setAsteroids] = useState([]);
    const [dataFetched, setDataFetched] = useState(false);
    const [outlineColor, setOutlineColor] = useState('black');
    const [bgColor, setBgColor] = useState('251A21');
    const [sortBy, setSortBy] = useState('size'); // 'size' or 'distance'
    const [viewMode, setViewMode] = useState('2d');

    const handleDateSubmit = async selectedDate => {
        setDate(selectedDate);
        let data;
        try {
            data = await fetchAsteroidData(selectedDate);
        } catch (err) {
            alert('Unable to fetch asteroid data:\n' + err.message);
            return;
        }
        const processed = processAsteroidData(data, selectedDate);
        setAsteroids(processed);
        setDataFetched(true);
    };

    const processAsteroidData = (data, userDate) => {
        if (!data.near_earth_objects) return [];
        return (data.near_earth_objects[userDate] || []).map(item => ({
            name: item.name,
            estDimAvgM:
                (item.estimated_diameter.meters.estimated_diameter_min +
                    item.estimated_diameter.meters.estimated_diameter_max) /
                2,
            missDistKM: parseFloat(item.close_approach_data[0].miss_distance.kilometers),
            color: getRandomColor(),
        }));
    };

    const getRandomColor = () => {
        const colors = require('../ColorConstants').default;
        return colors[Math.floor(Math.random() * colors.length)];
    };

    // Determine sorted data for table
    const tableData = [...asteroids].sort((a, b) => {
        if (sortBy === 'size') {
            return a.estDimAvgM - b.estDimAvgM;
        }
        return a.missDistKM - b.missDistKM;
    });

    return (
        <div style={{ textAlign: 'center', padding: '1rem' }}>
            <h1>Asteroid Data Visualizer</h1>
            <p>
                Generative data art capturing today's passing by asteroids created using NASA NeoWs API.
                Select a date to create a data visualization that is created from the
                passing by asteroids on that day.

                Each circle is an asteroid. The largest asteroids are in the back and the smallest
                are in the front. The closer to the blue horizon on the bottom, the closer the pass-by distance to the Earth. Colours are randomly selected from a library.
            </p>

            <DateInput onSubmit={handleDateSubmit} />

            {dataFetched && (
                <>
                    <div style={{ margin: '1rem' }}>
                        <label>Background:</label>{' '}
                        <select
                            value={bgColor}
                            onChange={e => setBgColor(e.target.value)}
                        >
                            <option value="251A21">Deep Purple</option>
                            <option value="0f0d25">Dark Blue</option>
                            <option value="ffffff">White</option>
                            <option value="000000">Black</option>
                        </select>
                    </div>

                    <div style={{ margin: '1rem' }}>
                        <label>Outline:</label>{' '}
                        <select
                            value={outlineColor}
                            onChange={e => setOutlineColor(e.target.value)}
                        >
                            <option value="white">White</option>
                            <option value="black">Black</option>
                            <option value="none">None</option>
                        </select>
                    </div>

                    <div style={{ margin: '1rem' }}>
                        <label>Sort table by:</label>{' '}
                        <select
                            value={sortBy}
                            onChange={e => setSortBy(e.target.value)}
                        >
                            <option value="size">Size</option>
                            <option value="distance">Distance</option>
                        </select>
                    </div>

                    <div className="view-mode-toggle" aria-label="Visualization view mode">
                        <button
                            type="button"
                            className={viewMode === '2d' ? 'view-mode-toggle__button active' : 'view-mode-toggle__button'}
                            onClick={() => setViewMode('2d')}
                        >
                            2D View
                        </button>
                        <button
                            type="button"
                            className={viewMode === '3d' ? 'view-mode-toggle__button active' : 'view-mode-toggle__button'}
                            onClick={() => setViewMode('3d')}
                        >
                            3D View
                        </button>
                    </div>

                    {/* show mint button after visualization */}
                    <AsteroidMinter date={date} asteroids={asteroids} outlineColor={outlineColor} bgColor={bgColor} />
                    <DownloadButtons asteroids={asteroids} date={date} />


                    {viewMode === '2d' ? (
                        <div className="asteroid-canvas-container">
                            <Sketch
                                className="asteroid-canvas"
                                asteroids={asteroids}
                                outlineColor={outlineColor}
                                bgColor={bgColor}
                            />
                        </div>
                    ) : (
                        <AsteroidScene3D
                            asteroids={asteroids}
                            outlineColor={outlineColor}
                            bgColor={bgColor}
                        />
                    )}



                    {/* Data table */}
                    <table className="asteroid-table" style={{ margin: '1rem auto', borderCollapse: 'collapse' }}>
                        <thead>
                        <tr>
                            <th>Name</th>
                            <th>Avg Diameter (m)</th>
                            <th>Miss Distance (km)</th>
                            <th>Color</th>
                        </tr>
                        </thead>
                        <tbody>
                        {tableData.map(a => (
                            <tr key={a.name}>
                                <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                    {a.name}
                                </td>
                                <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                    {a.estDimAvgM.toFixed(2)}
                                </td>
                                <td style={{ padding: '0.5rem', border: '1px solid #ccc' }}>
                                    {a.missDistKM.toFixed(2)}
                                </td>
                                <td style={{
                                    padding: '0.5rem',
                                    border: '1px solid #ccc',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <div style={{
                                        width: '16px',
                                        height: '16px',
                                        backgroundColor: a.color,
                                        border: '1px solid #000',
                                        marginRight: '0.5rem'
                                    }} />
                                    <span>{a.color}</span>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
}
